import { DateOnly } from '@if/core/date'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  ControlledToggleButtonGroup,
  FieldLabel,
  FormMethods,
  useFormDialog,
  useIfUser,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Stack, ToggleButton } from '@mui/material'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { DEFAULT_PAYMENT_METHOD } from '@src/types/constants'
import { Repeat } from 'lucide-react'
import { JSX, useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import ReturnToCalendarAction, { RETURN_TO_CALENDAR, ToolResult } from '../EditPayments-tool'
import {
  createFirstPaymentForRecurring,
  generateRecurringPaymentByEndDate,
  generateRecurringPaymentByNumberOfPayments,
} from './LoanAddRecurringPayment-helpers'
import {
  AddRecurringPaymentSchemaType,
  EAddRecurringPaymentOption,
  getAddRecurringPaymentSupportedFrequencies,
  useAddRecurringPaymentSchema,
} from './LoanAddRecurringPayment-schema'

interface LoanAddRecurringPaymentBodyProps {
  form: FormMethods<AddRecurringPaymentSchemaType>
  paymentMethodOptions: JSX.Element[]
  loanActivationDate: DateOnly
}

const LoanAddRecurringPaymentBody = ({
  form,
  paymentMethodOptions,
  loanActivationDate,
}: LoanAddRecurringPaymentBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  const frequencyOptions = useTranslatedOptionEnum(
    'EPaymentFrequency',
    getAddRecurringPaymentSupportedFrequencies(),
    'enum',
  )

  const option = useWatch({ control, name: 'option' })
  const paymentMethod = useWatch({ control, name: 'paymentMethod' })

  // The two ways of ending the series are exclusive, so picking one clears the other's field
  // rather than leaving a stale value the schema would still have to rule on.
  const handleOptionChange = useCallback(
    (value: string | null) => {
      if (value === EAddRecurringPaymentOption.EndDate) setValue('numberOfPayments', null)
      else if (value === EAddRecurringPaymentOption.NumberOfPayments) setValue('endDate', null)
    },
    [setValue],
  )

  const minStartDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(paymentMethod, loanActivationDate, canBypassAcpLock),
    [paymentMethod, canBypassAcpLock, loanActivationDate],
  )

  return (
    <Stack spacing={1}>
      <ControlledNumberField
        control={control}
        name="amount"
        isMoney
        label={t('loan.payment.addRecurringPayments.amount')}
        autoFocus
      />
      <Stack direction="row" spacing={1}>
        <ControlledSelect
          control={control}
          name="frequency"
          options={frequencyOptions}
          label={t('loan.payment.addRecurringPayments.frequency')}
          disableEmptyOption
        />
        <ControlledDateOnlyPicker
          control={control}
          name="startDate"
          label={t('loan.payment.addRecurringPayments.startDate')}
          minDate={minStartDate}
        />
      </Stack>

      <div>
        <FieldLabel>{t('loan.payment.addRecurringPayments.options.label')}</FieldLabel>
        <ControlledToggleButtonGroup
          control={control}
          name="option"
          exclusive
          required
          fullWidth
          color="primary"
          onChange={handleOptionChange}
        >
          <ToggleButton value={EAddRecurringPaymentOption.NumberOfPayments}>
            {t('loan.payment.addRecurringPayments.options.numberOfPayments')}
          </ToggleButton>
          <ToggleButton value={EAddRecurringPaymentOption.EndDate}>
            {t('loan.payment.addRecurringPayments.options.endDate')}
          </ToggleButton>
        </ControlledToggleButtonGroup>
      </div>
      {option === EAddRecurringPaymentOption.NumberOfPayments && (
        <ControlledNumberField
          control={control}
          name="numberOfPayments"
          label={t('loan.payment.addRecurringPayments.numberOfPayments')}
          disableDecimals
        />
      )}
      {option === EAddRecurringPaymentOption.EndDate && (
        <ControlledDateOnlyPicker
          control={control}
          name="endDate"
          label={t('loan.payment.addRecurringPayments.endDate')}
        />
      )}
      <ControlledSelect
        control={control}
        name="paymentMethod"
        options={paymentMethodOptions}
        label={t('loan.paymentMethod')}
        disableEmptyOption
      />
      <ControlledTextField control={control} name="note" label={t('loan.payment.description')} />
      <ControlledCheckbox control={control} name="sendSmsReminder" label={t('loan.payment.sendSMSReminder')} />
    </Stack>
  )
}

interface UseLoanAddRecurringPaymentDialogProps {
  loanActivationDate: DateOnly
  loanEndDate: DateOnly
  paymentMethodOptions: JSX.Element[]
}

/** Stages a whole series of payments on the editor's table, in one go. */
export const useLoanAddRecurringPaymentDialog = ({
  loanActivationDate,
  loanEndDate,
  paymentMethodOptions,
}: UseLoanAddRecurringPaymentDialogProps) => {
  const { t } = useTranslation()
  const schema = useAddRecurringPaymentSchema(loanActivationDate, loanEndDate)

  return useFormDialog<AddRecurringPaymentSchemaType, void, ToolResult<EditPaymentSchemaType[]>>({
    icon: Repeat,
    title: t('loan.payment.addRecurringPayments.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    defaultValues: { paymentMethod: DEFAULT_PAYMENT_METHOD, sendSmsReminder: true },
    successMessage: false,
    additionalActions: (ctx) => <ReturnToCalendarAction onReturn={() => ctx.close(RETURN_TO_CALENDAR)} />,
    content: ({ form }) => (
      <LoanAddRecurringPaymentBody
        form={form}
        paymentMethodOptions={paymentMethodOptions}
        loanActivationDate={loanActivationDate}
      />
    ),
    onSubmit: (data) => {
      const firstRecurringPayment = createFirstPaymentForRecurring(data)
      switch (data.option) {
        case EAddRecurringPaymentOption.NumberOfPayments:
          return generateRecurringPaymentByNumberOfPayments(
            firstRecurringPayment,
            data.frequency,
            data.numberOfPayments!,
          )
        case EAddRecurringPaymentOption.EndDate:
          return generateRecurringPaymentByEndDate(firstRecurringPayment, data.frequency, data.endDate!)
        default:
          throw new Error('Unknown option')
      }
    },
  })
}
